import {
	CodeBlock,
	Endpoint,
	Alert,
	Table,
	ParamList,
	Param,
	Step,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function GamesCoinflip() {
	return (
		<div className={styles.docsPage}>
			<h1>Coin Flip Game</h1>
			<p className={styles.lead}>
				The simplest provably fair game - predict whether the coin lands on
				heads or tails. A 50/50 chance with a 1.98x payout, perfect for quick
				bets and straightforward integration.
			</p>

			<Alert variant="info" title="House Edge">
				The Coin Flip game has a fixed 1% house edge. With equal 50% probability
				for each outcome, the payout multiplier is 1.98x (99% / 50%).
			</Alert>

			<h2>Game Overview</h2>
			<p>
				Coin Flip is the most straightforward game in the Ringfort Studio
				catalog. Players simply choose heads or tails, place their bet, and the
				server flips a provably fair coin. If the prediction matches the
				outcome, the player wins 1.98x their bet.
			</p>

			<h3>Key Features</h3>
			<ul>
				<li>
					<strong>Simple mechanics:</strong> Binary choice between heads and
					tails
				</li>
				<li>
					<strong>Fixed payout:</strong> 1.98x multiplier on every win
				</li>
				<li>
					<strong>Fast resolution:</strong> Instant results with minimal payload
				</li>
				<li>
					<strong>Easy to verify:</strong> Single bit outcome simplifies
					verification
				</li>
			</ul>

			<h3>How It Works</h3>
			<ol>
				<li>
					Player selects their prediction: <code>heads</code> or{' '}
					<code>tails</code>
				</li>
				<li>Player places their bet amount</li>
				<li>
					The server generates a provably fair coin flip result using
					HMAC-SHA256
				</li>
				<li>
					If the result matches the prediction, the player receives 1.98x their
					bet
				</li>
			</ol>

			<h3>Payout Details</h3>
			<Table
				columns={[
					{ key: 'outcome', header: 'Outcome' },
					{ key: 'probability', header: 'Probability' },
					{ key: 'multiplier', header: 'Multiplier' },
					{ key: 'example', header: 'Example ($10 bet)' },
				]}
				data={[
					{
						outcome: 'Win (correct prediction)',
						probability: '50%',
						multiplier: '1.98x',
						example: '$19.80 payout ($9.80 profit)',
					},
					{
						outcome: 'Lose (incorrect prediction)',
						probability: '50%',
						multiplier: '0x',
						example: '$0 payout ($10 loss)',
					},
				]}
			/>

			<h2>Bet Parameters</h2>
			<ParamList>
				<Param name="amount" type="number" required>
					The bet amount in the player's currency. Must be positive and within
					the operator's configured min/max limits.
				</Param>
				<Param name="prediction" type="string" required>
					The player's prediction for the coin flip result. Must be either{' '}
					<code>"heads"</code> or <code>"tails"</code>.
				</Param>
				<Param name="currency" type="string">
					Optional currency code (e.g., "USD", "BTC"). Defaults to the session's
					primary currency.
				</Param>
			</ParamList>

			<h2>API Endpoints</h2>

			<h3>Place Coin Flip Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/games/coinflip/bet"
				description="Place a new coin flip bet and receive the result"
			/>

			<h4>Request Body</h4>
			<CodeBlock language="json" title="Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "amount": 10.00,
  "prediction": "heads",
  "currency": "USD",
  "clientSeed": "flip-seed-789"
}`}
			</CodeBlock>

			<h4>Success Response</h4>
			<CodeBlock language="json" title="Response - Win">
				{`{
  "success": true,
  "data": {
    "betId": "bet_coinflip_ghi789jkl",
    "game": "coinflip",
    "amount": 10.00,
    "currency": "USD",
    "prediction": "heads",
    "result": "heads",
    "multiplier": 1.98,
    "win": true,
    "payout": 19.80,
    "profit": 9.80,
    "balanceAfter": 109.80,
    "timestamp": "2024-01-15T16:45:12.789Z",
    "provablyFair": {
      "serverSeedHash": "c3d4e5f6a7b8...",
      "clientSeed": "flip-seed-789",
      "nonce": 23,
      "serverSeed": null
    }
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Response - Loss">
				{`{
  "success": true,
  "data": {
    "betId": "bet_coinflip_mno012pqr",
    "game": "coinflip",
    "amount": 10.00,
    "currency": "USD",
    "prediction": "heads",
    "result": "tails",
    "multiplier": 1.98,
    "win": false,
    "payout": 0,
    "profit": -10.00,
    "balanceAfter": 90.00,
    "timestamp": "2024-01-15T16:45:45.123Z",
    "provablyFair": {
      "serverSeedHash": "c3d4e5f6a7b8...",
      "clientSeed": "flip-seed-789",
      "nonce": 24,
      "serverSeed": null
    }
  }
}`}
			</CodeBlock>

			<h3>Get Coin Flip Game Info</h3>
			<Endpoint
				method="GET"
				path="/api/v1/games/coinflip/info"
				description="Get game configuration and limits"
			/>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "game": "coinflip",
    "name": "Coin Flip",
    "description": "Classic heads or tails prediction game",
    "houseEdge": 1.0,
    "minBet": 0.10,
    "maxBet": 10000.00,
    "multiplier": 1.98,
    "predictions": ["heads", "tails"],
    "outcomes": ["heads", "tails"]
  }
}`}
			</CodeBlock>

			<h2>Complete Integration Example</h2>
			<p>
				Here is a complete TypeScript example showing how to integrate the Coin
				Flip game:
			</p>

			<CodeBlock language="typescript" title="coinflip-integration.ts" showLineNumbers>
				{`interface CoinflipBetParams {
  sessionToken: string;
  amount: number;
  prediction: 'heads' | 'tails';
  clientSeed?: string;
}

interface CoinflipBetResult {
  betId: string;
  prediction: 'heads' | 'tails';
  result: 'heads' | 'tails';
  win: boolean;
  payout: number;
  profit: number;
  multiplier: number;
  provablyFair: {
    serverSeedHash: string;
    clientSeed: string;
    nonce: number;
  };
}

const API_BASE = 'https://api.ringfort.studio/v1';
const COINFLIP_MULTIPLIER = 1.98;

// Place a coin flip bet
async function placeCoinflipBet(
  params: CoinflipBetParams
): Promise<CoinflipBetResult> {
  const clientSeed = params.clientSeed || generateClientSeed();

  const response = await fetch(\`\${API_BASE}/games/coinflip/bet\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionToken: params.sessionToken,
      amount: params.amount,
      prediction: params.prediction,
      clientSeed,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Bet placement failed');
  }

  const result = await response.json();
  return result.data;
}

// Generate a random client seed
function generateClientSeed(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let seed = '';
  for (let i = 0; i < 16; i++) {
    seed += chars[Math.floor(Math.random() * chars.length)];
  }
  return seed;
}

// Calculate expected value for a coin flip bet
function calculateExpectedValue(betAmount: number): number {
  // EV = (winProbability * winAmount) - (loseProbability * loseAmount)
  // EV = (0.5 * betAmount * 0.98) - (0.5 * betAmount)
  // EV = betAmount * (0.5 * 0.98 - 0.5)
  // EV = betAmount * -0.01
  return betAmount * -0.01; // Always -1% due to house edge
}

// Simple UI component for coin flip
class CoinflipGame {
  private sessionToken: string;
  private balance: number;

  constructor(sessionToken: string, initialBalance: number) {
    this.sessionToken = sessionToken;
    this.balance = initialBalance;
  }

  async flip(amount: number, prediction: 'heads' | 'tails'): Promise<void> {
    if (amount > this.balance) {
      console.error('Insufficient balance');
      return;
    }

    console.log(\`Flipping coin... Prediction: \${prediction}, Bet: \$\${amount}\`);

    try {
      const result = await placeCoinflipBet({
        sessionToken: this.sessionToken,
        amount,
        prediction,
      });

      // Update local balance
      this.balance = this.balance + result.profit;

      // Display result
      console.log(\`\\nResult: \${result.result.toUpperCase()}\`);

      if (result.win) {
        console.log(\`You WON! Payout: \$\${result.payout.toFixed(2)}\`);
        console.log(\`Profit: +\$\${result.profit.toFixed(2)}\`);
      } else {
        console.log(\`You LOST! Better luck next time.\`);
        console.log(\`Loss: \$\${Math.abs(result.profit).toFixed(2)}\`);
      }

      console.log(\`New balance: \$\${this.balance.toFixed(2)}\`);
      console.log(\`Bet ID: \${result.betId}\`);
    } catch (error) {
      console.error('Flip failed:', error);
    }
  }

  getBalance(): number {
    return this.balance;
  }
}

// Example usage
async function main() {
  const game = new CoinflipGame('your-session-token', 100.00);

  // Place a bet on heads
  await game.flip(10.00, 'heads');

  // Place a bet on tails
  await game.flip(5.00, 'tails');

  console.log(\`\\nFinal balance: \$\${game.getBalance().toFixed(2)}\`);
}

main();`}
			</CodeBlock>

			<h2>Provably Fair Verification</h2>
			<p>
				The Coin Flip result is determined by the provably fair system using
				HMAC-SHA256. The process is simple since there are only two possible
				outcomes.
			</p>

			<Step number={1} title="Server Commits to Seed">
				<p>
					You receive a <code>serverSeedHash</code> before placing bets. This is
					the SHA-256 hash of the server seed, committing the server without
					revealing the seed.
				</p>
			</Step>

			<Step number={2} title="You Provide Client Seed">
				<p>
					Submit your own <code>clientSeed</code> to ensure you influence the
					outcome. This can be any string you choose.
				</p>
			</Step>

			<Step number={3} title="Result Generation">
				<p>
					The server combines seeds to generate the result. The first byte of
					the HMAC determines heads (even) or tails (odd).
				</p>
				<CodeBlock language="typescript">
					{`const hash = createHmac('sha256', serverSeed)
  .update(\`\${clientSeed}:\${nonce}\`)
  .digest('hex');

// First byte determines outcome
const firstByte = parseInt(hash.substring(0, 2), 16);
const result = firstByte % 2 === 0 ? 'heads' : 'tails';`}
				</CodeBlock>
			</Step>

			<Step number={4} title="Verify After Rotation">
				<p>
					When you rotate your server seed, use the revealed seed to verify past
					results.
				</p>
			</Step>

			<CodeBlock language="typescript" title="Complete Verification">
				{`import { createHash, createHmac } from 'crypto';

interface CoinflipVerification {
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  expectedResult: 'heads' | 'tails';
}

function verifyCoinflip(data: CoinflipVerification): boolean {
  // Step 1: Verify the server seed hash
  const computedHash = createHash('sha256')
    .update(data.serverSeed)
    .digest('hex');

  if (computedHash !== data.serverSeedHash) {
    console.error('Server seed hash does not match!');
    console.error(\`Expected: \${data.serverSeedHash}\`);
    console.error(\`Computed: \${computedHash}\`);
    return false;
  }

  console.log('Server seed hash verified.');

  // Step 2: Calculate the coin flip result
  const hmac = createHmac('sha256', data.serverSeed)
    .update(\`\${data.clientSeed}:\${data.nonce}\`)
    .digest('hex');

  const firstByte = parseInt(hmac.substring(0, 2), 16);
  const calculatedResult = firstByte % 2 === 0 ? 'heads' : 'tails';

  console.log(\`HMAC (first 8 chars): \${hmac.substring(0, 8)}\`);
  console.log(\`First byte value: \${firstByte}\`);
  console.log(\`Calculated result: \${calculatedResult}\`);

  // Step 3: Compare with expected result
  if (calculatedResult !== data.expectedResult) {
    console.error(\`Result mismatch! Expected \${data.expectedResult}, got \${calculatedResult}\`);
    return false;
  }

  console.log('Result verified successfully!');
  return true;
}

// Example verification
const verificationData: CoinflipVerification = {
  serverSeed: 'abc123secretseed',
  serverSeedHash: 'computed_sha256_hash_of_server_seed',
  clientSeed: 'flip-seed-789',
  nonce: 23,
  expectedResult: 'heads',
};

verifyCoinflip(verificationData);`}
			</CodeBlock>

			<Alert variant="success" title="Simple Verification">
				Coin Flip has the simplest verification of all games - just one byte
				determines the outcome. This makes it easy for players to verify
				fairness with minimal technical knowledge.
			</Alert>

			<h2>Batch Betting</h2>
			<p>
				For high-frequency betting, you can use the batch endpoint to place
				multiple coin flips in a single request:
			</p>

			<Endpoint
				method="POST"
				path="/api/v1/games/coinflip/batch"
				description="Place multiple coin flip bets in one request (max 100)"
			/>

			<CodeBlock language="json" title="Batch Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "bets": [
    { "amount": 1.00, "prediction": "heads" },
    { "amount": 1.00, "prediction": "tails" },
    { "amount": 1.00, "prediction": "heads" },
    { "amount": 2.00, "prediction": "tails" },
    { "amount": 5.00, "prediction": "heads" }
  ],
  "clientSeed": "batch-flip-001"
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Batch Response">
				{`{
  "success": true,
  "data": {
    "batchId": "batch_flip_xyz123",
    "totalBets": 5,
    "totalWagered": 10.00,
    "totalPayout": 13.86,
    "totalProfit": 3.86,
    "wins": 3,
    "losses": 2,
    "results": [
      { "betId": "bet_1", "prediction": "heads", "result": "heads", "win": true, "payout": 1.98 },
      { "betId": "bet_2", "prediction": "tails", "result": "heads", "win": false, "payout": 0 },
      { "betId": "bet_3", "prediction": "heads", "result": "tails", "win": false, "payout": 0 },
      { "betId": "bet_4", "prediction": "tails", "result": "tails", "win": true, "payout": 3.96 },
      { "betId": "bet_5", "prediction": "heads", "result": "heads", "win": true, "payout": 9.90 }
    ],
    "balanceAfter": 103.86
  }
}`}
			</CodeBlock>

			<h2>Error Handling</h2>
			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'message', header: 'Message' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{
						code: 'INVALID_PREDICTION',
						message: 'Prediction must be "heads" or "tails"',
						solution: 'Use exactly "heads" or "tails" as the prediction value',
					},
					{
						code: 'INSUFFICIENT_BALANCE',
						message: 'Insufficient balance for bet',
						solution: 'Check player balance before placing bet',
					},
					{
						code: 'BET_BELOW_MINIMUM',
						message: 'Bet amount below minimum',
						solution: 'Increase bet to meet minimum requirement',
					},
					{
						code: 'BET_ABOVE_MAXIMUM',
						message: 'Bet amount exceeds maximum',
						solution: 'Reduce bet to stay within limits',
					},
					{
						code: 'BATCH_TOO_LARGE',
						message: 'Batch exceeds maximum of 100 bets',
						solution: 'Split into multiple batch requests',
					},
					{
						code: 'SESSION_EXPIRED',
						message: 'Session token has expired',
						solution: 'Request a new session token from the operator',
					},
				]}
			/>

			<h2>Use Cases</h2>
			<p>Coin Flip is ideal for several scenarios:</p>

			<ul>
				<li>
					<strong>Quick decisions:</strong> When players want fast, simple bets
					without complex strategy
				</li>
				<li>
					<strong>Double or nothing:</strong> Classic gambling mechanic that
					players instantly understand
				</li>
				<li>
					<strong>Testing integration:</strong> Simple API makes it perfect for
					initial integration testing
				</li>
				<li>
					<strong>Promotional games:</strong> Easy to understand for marketing
					campaigns
				</li>
				<li>
					<strong>Mobile-first:</strong> Minimal UI requirements work well on
					small screens
				</li>
			</ul>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/provably-fair">
							Learn more about Provably Fair verification
						</a>
					</li>
					<li>
						<a href="/docs/games-dice">
							Try Dice for more customizable risk/reward
						</a>
					</li>
					<li>
						<a href="/docs/games-crash">
							Explore the multiplayer Crash game
						</a>
					</li>
				</ul>
			</div>
		</div>
	);
}
